/**
 * The blocks this class meets in.
 *
 * School setup rather than teaching: a teacher reads it, an administrator
 * shapes it, which is why it sorts below the daily work in the class nav.
 */
import { readFragment } from 'gql.tada';
import { useOutletContext, useParams } from 'react-router-dom';
import ClassScheduleSection from '../../Schedules/ClassScheduleSection';
import { ScheduleBlockFields } from '../../Schedules/useScheduleBlocks';
import {
  termOption,
  type ScheduleBlockValue,
  type ClassOption,
  type StaffOption,
  type StudentOption,
} from '../../Schedules/ScheduleBlockDialog';
import type { ClassDetailContext } from '../classDetailTypes';

function ClassSchedulePage() {
  const { schoolSlug, yearSlug } = useParams();
  const { offering, isAdmin, activeEntries, refetch } = useOutletContext<ClassDetailContext>();

  const schoolYear = offering.course.schoolYear;

  return (
    <ClassScheduleSection
      schoolId={schoolYear.school.id}
      schoolYearId={schoolYear.id}
      schoolYearStart={String(schoolYear.startDate)}
      schoolYearEnd={String(schoolYear.endDate)}
      classOffering={{
        id: offering.id,
        displayName: offering.displayName,
        courseId: offering.course.id,
        courseName: offering.course.name,
        section: offering.section,
        term: termOption(offering.term),
      }}
      classOfferings={offering.scheduleSeriesOfferings
        .map((classOption) => ({
          id: classOption.id,
          displayName: classOption.displayName,
          courseId: offering.course.id,
          courseName: offering.course.name,
          section: classOption.section,
          term: termOption(classOption.term),
        })) as ReadonlyArray<ClassOption>}
      blocks={(offering.scheduleBlocks ?? []).map(
        (block) => readFragment(ScheduleBlockFields, block) as unknown as ScheduleBlockValue
      )}
      staff={(schoolYear.school.staffMembers ?? []) as ReadonlyArray<StaffOption>}
      students={activeEntries.map((entry) => ({ person: entry.person, gradeLevel: null })) as ReadonlyArray<StudentOption>}
      terms={schoolYear.terms.map(termOption)}
      gradeLevels={[...(schoolYear.school.gradeLevelsSetting?.items ?? [])]
        .sort((first, second) => first.order - second.order)
        .map((grade) => grade.description)}
      schoolSlug={schoolSlug!}
      yearSlug={yearSlug!}
      canManage={isAdmin}
      onChanged={async () => { await refetch(); }}
    />
  );
}

export default ClassSchedulePage;
